import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import ChangePasswordForm from './components/ChangePasswordForm';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { hasAdminPermission } from './utils/adminPermissions';
import { apiService } from './services/api';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import AboutContentPage from './pages/AboutContentPage';
import DashboardPage from './pages/DashboardPage';
import ProgramPage from './pages/ProgramPage';
import ApplicationReviewPage from './pages/ApplicationReviewPage';
import MyApplicationPage from './pages/MyApplicationPage';
import ProfilePage from './pages/ProfilePage';
import ScholarManagementPage from './pages/ScholarManagementPage';
import ScholarDetailPage from './pages/ScholarDetailPage';
import DocumentRequirementsPage from './pages/DocumentRequirementsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import NotificationsPage from './pages/NotificationsPage';
import ReportsPage from './pages/ReportsPage';
import UserManagementPage from './pages/UserManagementPage';
import DeletionReportPage from './pages/DeletionReportPage';
import PayrollPage from './pages/PayrollPage';
import RenewalRequestsPage from './pages/RenewalRequestsPage';
import SettingsPage from './pages/SettingsPage';
import HomeContentPage from './pages/HomeContentPage';
import UserAgreementPage from './pages/UserAgreementPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import Sidebar from './components/Sidebar';
import ConsentBanner from './components/ConsentBanner';
import { UserRole } from './types';
import './styles.css';

const SIDEBAR_PATHS = [
  '/dashboard',
  '/programs',
  '/applications',
  '/my-application',
  '/profile',
  '/scholars',
  '/document-requirements',
  '/announcements',
  '/notifications',
  '/reports',
  '/users',
  '/deletion-requests',
  '/payroll',
  '/renewals',
  '/settings',
  '/home-content',
  '/user-agreement',
  '/about',
  '/about-content',
  '/system-settings'
];

/**
 * Shown instead of the whole app while the signed-in account still has a
 * password someone else chose. The server refuses every other request
 * until it's replaced, so there is nothing else this screen could offer.
 */
function ForcedPasswordChange() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="login-container">
      <div className="login-card">
        <h2 style={{ marginBottom: '0.5rem' }}>Choose your password</h2>
        <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
          Signed in as <strong>{user?.email}</strong>
        </p>
        <ChangePasswordForm forced />
        <button
          className="btn btn-outline btn-sm"
          type="button"
          style={{ marginTop: '1rem', width: '100%' }}
          onClick={() => {
            logout();
            navigate('/login');
          }}
        >
          Log out
        </button>
      </div>
    </div>
  );
}

// How often an open page re-checks whether the system is closed, so it
// closes (and reopens) for people already on the site, not only new ones.
const MAINTENANCE_POLL_MS = 60_000;

/**
 * Whether the system is closed to students right now. An unanswered
 * check counts as open: a busy server must not lock everyone out — the
 * server enforces the real rule on every request anyway.
 */
function useMaintenanceMode(): boolean {
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      apiService
        .getMaintenanceMode()
        .then((enabled) => {
          if (!cancelled) setClosed(enabled);
        })
        .catch(() => {
          // Unknown: keep whatever was last known.
        });
    };
    check();
    const timer = setInterval(check, MAINTENANCE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);
  return closed;
}

const STAFF_ROLES = [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.VIEWER];

function ClosedNotice() {
  return (
    <div className="page-root">
      <div className="page-scroll">
        <div className="login-container">
          <div className="login-card" style={{ textAlign: 'center' }}>
            <h2 style={{ marginBottom: '0.75rem' }}>The system is temporarily closed</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              The ECEAP scholarship system is closed for now. Please try again later — your account and everything
              you have submitted are safe.
            </p>
            <p style={{ fontSize: '0.8rem', margin: 0 }}>
              Scholarship staff: <Link to="/login">sign in here</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppLayout() {
  const location = useLocation();
  const { user } = useAuth();
  const closed = useMaintenanceMode();

  // Closed for maintenance: everyone but staff sees the notice, on every
  // page except sign-in (so staff can still get in; a student who tries
  // is told the system is closed).
  if (closed && !(user && STAFF_ROLES.includes(user.role)) && location.pathname !== '/login') {
    return <ClosedNotice />;
  }

  if (user?.mustChangePassword) {
    return (
      <div className="page-root">
        <div className="page-scroll">
          <ForcedPasswordChange />
        </div>
      </div>
    );
  }

  const showSidebar = SIDEBAR_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  return (
    <div className={showSidebar ? 'app-layout' : 'page-root'}>
      {showSidebar && <Sidebar />}
      <div className={showSidebar ? 'app-main' : 'page-scroll'}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Public on purpose — these have to be readable before
              registering, since registering is the point at which
              someone hands over an ID number and a household income. */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          {/* Public view; the Super-Admin editor is /about-content below. */}
          <Route path="/about" element={<AboutPage />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.VIEWER]}>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.VIEWER]}>
                <ApplicationReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-application"
            element={
              <ProtectedRoute allowedRoles={[UserRole.APPLICANT]}>
                <MyApplicationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowedRoles={[UserRole.APPLICANT]}>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs"
            element={
              <ProtectedRoute>
                <ProgramPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scholars"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.VIEWER]}>
                <ScholarManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scholars/:id"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.VIEWER]}>
                <ScholarDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/document-requirements"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.VIEWER]}>
                <DocumentRequirementsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.VIEWER]} adminPermission="applicationReports">
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deletion-requests"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                <DeletionReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                <PayrollPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/renewals"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.VIEWER]} adminPermission="renewalRequests">
                <RenewalRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/announcements"
            element={
              <ProtectedRoute>
                <AnnouncementsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/home-content"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                <HomeContentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/about-content"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                <AboutContentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-agreement"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                <UserAgreementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-settings"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                <SystemSettingsPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {/* Outside the scrolling column so it stays put rather than
          travelling with the page content. */}
      <ConsentBanner />
    </div>
  );
}

/**
 * Carries the signed-in Admin's "themes" switch across to the theme
 * provider, which sits outside the auth tree and so can't read it itself.
 */
function ThemePermissionSync() {
  const { user } = useAuth();
  const { setThemesAllowed } = useTheme();
  const allowed = hasAdminPermission(user, 'themes');
  useEffect(() => setThemesAllowed(allowed), [allowed, setThemesAllowed]);
  return null;
}

function App() {
  return (
    // BASE_URL mirrors vite.config.ts's `base` (the domain root now that
    // the app is deployed via Render rather than a GitHub Pages subpath).
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <ThemeProvider>
        <AuthProvider>
          <ThemePermissionSync />
          <AppLayout />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
